import { useState, useMemo } from 'react';
import { Mail, MapPin, Phone, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import useAnalytics from '../hooks/useAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  useAnalytics('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const captcha = useMemo(() => {
    const a = Math.floor(Math.random() * 15) + 1;
    const b = Math.floor(Math.random() * 15) + 1;
    return { a, b, sum: a + b };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseInt(captchaAnswer) !== captcha.sum) {
      toast.error('Incorrect answer to the math question. Please try again.');
      return;
    }
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        captcha_num1: captcha.a,
        captcha_num2: captcha.b,
        captcha_answer: parseInt(captchaAnswer),
      });
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Failed to send message:', error);
      const msg = error.response?.data?.detail || 'Failed to send message. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 pt-20 flex items-center justify-center" data-testid="contact-success">
        <div className="max-w-md text-center px-6">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-8">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 className="font-serif text-3xl text-stone-900 mb-4">Message Sent!</h1>
          <p className="text-stone-600 mb-8">
            Thank you for reaching out. I'll get back to you as soon as possible, 
            usually within 24-48 hours.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: '', email: '', company: '', subject: '', message: '' });
            }}
            className="text-red-800 hover:text-red-900 font-medium"
            data-testid="send-another-btn"
          >
            Send another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="contact-page">
      <SEO
        title="Contact"
        description="Get in touch with Marta Scharle for assignments, collaborations, and editorial partnerships."
        url="/contact"
      />
      {/* Header */}
      <section className="py-16 md:py-24 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="text-sm tracking-widest uppercase text-red-800 font-medium mb-4">
            Get in Touch
          </p>
          <h1 className="font-serif text-5xl md:text-6xl text-stone-900 tracking-tight">
            Let's Work Together
          </h1>
          <p className="mt-6 text-stone-600 text-lg max-w-2xl">
            Looking for a travel journalist to cover your destination or story? 
            I'm available for assignments, collaborations, and editorial partnerships.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div className="lg:col-span-4" id="contact-info">
              <h2 className="font-serif text-2xl text-stone-900 mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-red-800" />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900 mb-1">Email</h3>
                    <a
                      href="mailto:marta.scharle@gmail.com"
                      className="text-stone-600 hover:text-red-800 transition-colors"
                    >
                      marta.scharle@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-red-800" />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900 mb-1">Location</h3>
                    <p className="text-stone-600">
                      Kathmandu, Nepal<br />
                      Available for assignments worldwide
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-red-800" />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900 mb-1">Response Time</h3>
                    <p className="text-stone-600">Usually within 24-48 hours</p>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="mt-12 p-6 bg-stone-100 rounded-sm">
                <h3 className="font-serif text-lg text-stone-900 mb-4">Available For</h3>
                <ul className="space-y-2 text-stone-600 text-sm">
                  <li>• Destination features and guides</li>
                  <li>• Cultural and documentary photography</li>
                  <li>• Adventure and trekking content</li>
                  <li>• Brand partnerships and campaigns</li>
                  <li>• Editorial consulting</li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-8">
              <div className="bg-white p-8 md:p-12 rounded-sm border border-stone-200">
                <h2 className="font-serif text-2xl text-stone-900 mb-8">Send a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-stone-700 mb-2 block">
                        Your Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="bg-stone-50 border-stone-200 focus:border-stone-400 rounded-sm"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-stone-700 mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="bg-stone-50 border-stone-200 focus:border-stone-400 rounded-sm"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company" className="text-stone-700 mb-2 block">
                        Publication / Company
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company or publication"
                        className="bg-stone-50 border-stone-200 focus:border-stone-400 rounded-sm"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-stone-700 mb-2 block">
                        Subject
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="What's this about?"
                        className="bg-stone-50 border-stone-200 focus:border-stone-400 rounded-sm"
                        data-testid="contact-subject-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-stone-700 mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell me about your project, assignment, or collaboration idea..."
                      className="bg-stone-50 border-stone-200 focus:border-stone-400 rounded-sm resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="captcha" className="text-stone-700 mb-2 block">
                      Spam check: What is {captcha.a} + {captcha.b}? *
                    </Label>
                    <Input
                      id="captcha"
                      name="captcha"
                      type="number"
                      required
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      placeholder="Your answer"
                      className="bg-stone-50 border-stone-200 focus:border-stone-400 rounded-sm max-w-[200px]"
                      data-testid="contact-captcha-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full md:w-auto bg-stone-900 text-stone-50 px-10 py-6 rounded-sm hover:bg-red-800 transition-colors duration-300 font-medium tracking-wide text-sm uppercase"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-stone-50"></div>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send size={16} /> Send Message
                      </span>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
